import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import ClinicalFormShell from './ClinicalFormShell';
import { mergeDentalIntake } from '../../lib/clinicalDentalSchema';
import { formatClinicalHistoryTitle } from '../../lib/clinicalFormTemplates';
import { DEFAULT_BRAND_NAME } from '../../lib/clinicSiteDefaults';
import { useClinicBranding } from '../../context/ClinicBrandingContext';
import { patchPatient, patchPatientRecord } from '../../lib/api';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

function FieldRow({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold">{label}</Label>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {children}
    </div>
  );
}

export default function DentalIntakeForm({
  recordId,
  patientId,
  patient,
  initialIntake,
  recordStatus,
  onSaved,
  onCompleted,
}) {
  const isComplete = recordStatus === 'complete';
  const [intake, setIntake] = useState(() => mergeDentalIntake(initialIntake));
  const [saving, setSaving] = useState(false);
  const { info: clinicInfo } = useClinicBranding();
  const formTitle = formatClinicalHistoryTitle(clinicInfo?.name, DEFAULT_BRAND_NAME);

  useEffect(() => {
    setIntake(mergeDentalIntake(initialIntake));
  }, [initialIntake]);

  const update = useCallback((key, value) => {
    setIntake((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveDraft = async (markComplete = false) => {
    const patientName = String(patient?.full_name || '').trim();
    if (markComplete && !patientName) {
      toast.error('Indique el nombre del paciente en el expediente');
      return;
    }
    if (markComplete && !intake.treatment_consent) {
      toast.error('Se requiere consentimiento de tratamiento para finalizar');
      return;
    }
    setSaving(true);
    try {
      if (patientId && intake.edad) {
        await patchPatient(patientId, { full_name: patientName });
      }
      const payload = {
        intake_adult: mergeDentalIntake({
          ...intake,
          header: {
            ...intake.header,
            form_date: intake.header?.form_date || new Date().toISOString().slice(0, 10),
          },
        }),
        consent: {
          patient_signature_name: intake.consent_name || patientName,
          clinical_photos_consent: Boolean(intake.photo_consent),
          authorized: Boolean(intake.treatment_consent),
          signed_at: markComplete ? new Date().toISOString() : undefined,
        },
        status: markComplete ? 'complete' : recordStatus === 'complete' ? 'complete' : 'draft',
      };
      const saved = await patchPatientRecord(recordId, payload);
      toast.success(
        markComplete ? 'Historia clínica guardada' : isComplete ? 'Cambios guardados' : 'Borrador guardado',
      );
      onSaved?.(saved);
      if (markComplete) onCompleted?.(saved);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const formDate = intake.header?.form_date || new Date().toISOString().slice(0, 10);

  return (
    <ClinicalFormShell
      title={formTitle}
      subtitle={intake.header?.folio ? `Folio ${intake.header.folio}` : 'Clínica dental'}
    >
      <div className="grid gap-4">
        <FieldRow label="Fecha">
          <Input
            type="date"
            className="h-11 max-w-[12rem]"
            value={formDate}
            onChange={(e) =>
              setIntake((prev) => ({
                ...prev,
                header: { ...prev.header, form_date: e.target.value },
              }))
            }
          />
        </FieldRow>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_6rem] gap-4">
          <FieldRow label="Nombre">
            <Input className="h-11" value={patient?.full_name || ''} disabled />
          </FieldRow>
          <FieldRow label="Edad">
            <Input
              className="h-11"
              inputMode="numeric"
              value={intake.edad}
              onChange={(e) => update('edad', e.target.value)}
            />
          </FieldRow>
        </div>

        <FieldRow label="Antecedentes médicos relevantes">
          <Textarea
            rows={3}
            value={intake.medical_history}
            onChange={(e) => update('medical_history', e.target.value)}
          />
        </FieldRow>
        <FieldRow label="Alergias">
          <Input className="h-11" value={intake.allergies} onChange={(e) => update('allergies', e.target.value)} />
        </FieldRow>
        <FieldRow label="Medicamentos actuales">
          <Input
            className="h-11"
            value={intake.medications}
            onChange={(e) => update('medications', e.target.value)}
          />
        </FieldRow>
        <FieldRow label="Motivo de consulta">
          <Textarea
            rows={2}
            value={intake.chief_complaint}
            onChange={(e) => update('chief_complaint', e.target.value)}
          />
        </FieldRow>
        <FieldRow label="Historial dental">
          <Textarea
            rows={3}
            value={intake.dental_history}
            onChange={(e) => update('dental_history', e.target.value)}
          />
        </FieldRow>
        <FieldRow
          label="Odontograma / piezas"
          hint="Anote hallazgos por pieza (ej. 16 caries, 36 endodoncia…)."
        >
          <Textarea rows={4} value={intake.odontogram} onChange={(e) => update('odontogram', e.target.value)} />
        </FieldRow>
        <FieldRow label="Examen clínico">
          <Textarea
            rows={3}
            value={intake.exam_findings}
            onChange={(e) => update('exam_findings', e.target.value)}
          />
        </FieldRow>
        <FieldRow label="Diagnóstico">
          <Input className="h-11" value={intake.diagnosis} onChange={(e) => update('diagnosis', e.target.value)} />
        </FieldRow>
        <FieldRow label="Plan de tratamiento">
          <Textarea
            rows={4}
            value={intake.treatment_plan}
            onChange={(e) => update('treatment_plan', e.target.value)}
          />
        </FieldRow>

        <div className="rounded-lg border border-dashed p-4 bg-muted/20 text-sm text-muted-foreground">
          En cada <strong>nota de evolución / seguimiento</strong> podrá subir fotos, radiografías y archivos PDF, e
          imprimir la plantilla. El nombre del médico y la cédula se toman de{' '}
          <strong>Settings → Historia clínica — médico responsable</strong>.
        </div>

        <div className="rounded-lg border p-4 space-y-3">
          <p className="text-sm font-semibold">Consentimiento</p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={intake.photo_consent}
              onChange={(e) => update('photo_consent', e.target.checked)}
            />
            Autorizo fotografías clínicas / radiografías para el expediente.
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={intake.treatment_consent}
              onChange={(e) => update('treatment_consent', e.target.checked)}
            />
            Autorizo el plan de tratamiento odontológico propuesto.
          </label>
          <FieldRow label="Nombre de quien autoriza">
            <Input
              className="h-11"
              value={intake.consent_name}
              onChange={(e) => update('consent_name', e.target.value)}
              placeholder={patient?.full_name || ''}
            />
          </FieldRow>
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" disabled={saving} onClick={() => void saveDraft(false)}>
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Guardando…' : isComplete ? 'Guardar cambios' : 'Guardar borrador'}
          </Button>
          {!isComplete ? (
            <Button type="button" disabled={saving} onClick={() => void saveDraft(true)}>
              {saving ? 'Guardando…' : 'Finalizar historia'}
            </Button>
          ) : null}
        </div>
      </div>
    </ClinicalFormShell>
  );
}
